import { create } from 'zustand';

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification interface
 */
export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

/**
 * Toast store state interface
 */
interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

/**
 * Generate unique toast ID
 */
function generateId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Default toast duration in milliseconds
 */
const DEFAULT_DURATION = 5000;

/**
 * Toast store for managing notifications.
 * Provides methods to show, dismiss, and clear toasts.
 */
export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = generateId();
    const newToast: Toast = {
      id,
      duration: DEFAULT_DURATION,
      dismissible: true,
      ...toast,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, newToast.duration);
    }

    return id;
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearToasts: () => set({ toasts: [] }),
}));

/**
 * Convenience functions for showing toasts
 */
export const toast = {
  success: (title: string, message?: string) => {
    const toastData: Omit<Toast, 'id'> = { type: 'success', title };
    if (message !== undefined) toastData.message = message;
    return useToastStore.getState().addToast(toastData);
  },

  error: (title: string, message?: string) => {
    const toastData: Omit<Toast, 'id'> = { type: 'error', title };
    if (message !== undefined) toastData.message = message;
    return useToastStore.getState().addToast(toastData);
  },

  warning: (title: string, message?: string) => {
    const toastData: Omit<Toast, 'id'> = { type: 'warning', title };
    if (message !== undefined) toastData.message = message;
    return useToastStore.getState().addToast(toastData);
  },

  info: (title: string, message?: string) => {
    const toastData: Omit<Toast, 'id'> = { type: 'info', title };
    if (message !== undefined) toastData.message = message;
    return useToastStore.getState().addToast(toastData);
  },

  dismiss: (id: string) => useToastStore.getState().removeToast(id),

  clear: () => useToastStore.getState().clearToasts(),
};
